import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Search,
  Trash2,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import {
  useDeleteMessageBatch,
  useMessageBatchesList,
  useUpdateMessageBatch,
  type AdminMessageBatch,
} from "@/lib/queries/messages"
import { ExportButton } from "@/components/ExportButton"
import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Messages (list) — V-I
// API:    POST /admin/message_list    (server pagination/sort/search)
//         POST /admin/message_delete  (delete batch)
//
// Shows one row per SEND EVENT (batch), not per recipient, so admins
// see the list at the granularity they composed at.
// ───────────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return v
}

function kindBadgeClass(kind: string): string {
  switch (kind) {
    case "voice":
      return "bg-violet-100 text-violet-900 dark:bg-violet-900/40 dark:text-violet-200"
    case "quote":
      return "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"
    case "text":
      return "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200"
    default:
      return "bg-secondary text-secondary-foreground"
  }
}

function previewText(b: AdminMessageBatch): string {
  const src = b.topic || b.body || b.preview || ""
  if (!src) return "—"
  return src.length > 80 ? `${src.slice(0, 77)}…` : src
}

export default function Messages() {
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounced(search, 300)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [sortyBy, setSortyBy] = useState<string | undefined>("sent_at")
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1)

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, limit, sortyBy, sortDirection])

  const params = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || undefined,
      sortyBy,
      sortDirection,
    }),
    [page, limit, debouncedSearch, sortyBy, sortDirection]
  )

  const messagesQ = useMessageBatchesList(params)
  const deleteMut = useDeleteMessageBatch()
  const updateMut = useUpdateMessageBatch()

  const [deleteBatch, setDeleteBatch] = useState<AdminMessageBatch | null>(
    null
  )
  // Edit batch state + the editable form fields.
  const [editBatch, setEditBatch] = useState<AdminMessageBatch | null>(null)
  const [editForm, setEditForm] = useState<{
    topic: string
    body: string
    preview: string
    content_tier: "general" | "premium"
  }>({ topic: "", body: "", preview: "", content_tier: "general" })

  const openEdit = (b: AdminMessageBatch) => {
    setEditForm({
      topic: b.topic || "",
      body: b.body || "",
      preview: b.preview || "",
      // V-CoachSub Phase 3 UX: seed from the doc's persisted tier so
      // the radio reflects the current state instead of always
      // defaulting to general (which would silently flip a premium
      // message back to general on save if the admin didn't touch it).
      content_tier: b.content_tier === "premium" ? "premium" : "general",
    })
    setEditBatch(b)
  }

  const handleEditSave = async () => {
    if (!editBatch) return
    await updateMut.mutateAsync({
      batch_id: editBatch._id,
      topic: editForm.topic,
      body: editForm.body,
      ...(editBatch.kind === "text" ? { preview: editForm.preview } : {}),
      content_tier: editForm.content_tier,
    })
    setEditBatch(null)
  }

  const onSortClick = (field: string) => {
    if (sortyBy === field) {
      setSortDirection((prev) => (prev === 1 ? -1 : 1))
    } else {
      setSortyBy(field)
      setSortDirection(-1)
    }
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortyBy !== field) return null
    return sortDirection === 1 ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    )
  }

  const totalPages = messagesQ.data?.totalPages ?? 0
  const totalDocs = messagesQ.data?.totalDocs ?? 0

  const handleDeleteConfirm = async () => {
    if (!deleteBatch) return
    await deleteMut.mutateAsync(deleteBatch._id)
    setDeleteBatch(null)
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-end sm:justify-between sm:space-y-0">
          <div>
            <CardTitle>Messages</CardTitle>
            <CardDescription>
              {totalDocs > 0
                ? `${totalDocs.toLocaleString()} total`
                : "Creator-to-follower sends (voice, quote, text)"}
            </CardDescription>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search creator, topic, body…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                aria-label="Search messages"
              />
            </div>
            <ExportButton<AdminMessageBatch>
              filenamePrefix="messages"
              totalEstimate={totalDocs}
              columns={[
                { label: "Creator Name", get: (b) => b.coach_name ?? "" },
                { label: "Kind", get: (b) => b.kind },
                {
                  label: "Preview",
                  get: (b) => b.topic || b.body || b.preview || "",
                },
                { label: "Recipients", get: (b) => b.recipientCount ?? 0 },
                {
                  label: "Read",
                  get: (b) =>
                    `${b.readCount ?? 0}/${b.recipientCount ?? 0}`,
                },
                {
                  label: "Sent",
                  get: (b) =>
                    b.sent_at ? new Date(b.sent_at).toISOString() : "",
                },
                { label: "Batch ID", get: (b) => b._id },
              ]}
              request={async (page, limit) => {
                const res = await api.post("/admin/message_list", {
                  page,
                  limit,
                  search: debouncedSearch || undefined,
                  sortyBy,
                  sortDirection,
                })
                return {
                  docs: res.data?.data?.docs ?? [],
                  totalDocs: res.data?.data?.totalDocs ?? 0,
                }
              }}
            />
            <Button asChild>
              <Link to="/messages/new">Send message</Link>
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="p-0">
          {messagesQ.isError && (
            <div
              role="alert"
              className="m-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load messages. Try refreshing.</span>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Creator</TableHead>
                <TableHead className="w-24">Kind</TableHead>
                <TableHead>Preview</TableHead>
                <SortableTH
                  field="recipientCount"
                  label="Recipients"
                  className="w-28"
                  {...{ onSortClick, SortIcon }}
                />
                <TableHead className="w-20">Read</TableHead>
                <SortableTH
                  field="sent_at"
                  label="Sent"
                  className="w-36"
                  {...{ onSortClick, SortIcon }}
                />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {messagesQ.isLoading && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="h-32 text-center text-muted-foreground"
                  >
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {!messagesQ.isLoading &&
                (messagesQ.data?.docs.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="h-32 text-center text-muted-foreground"
                    >
                      No messages match your filters.
                    </TableCell>
                  </TableRow>
                )}
              {messagesQ.data?.docs.map((b) => (
                <TableRow key={b._id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {b.coach_avatar_url ? (
                        <img
                          src={b.coach_avatar_url}
                          alt=""
                          loading="lazy"
                          className="h-9 w-9 rounded-full object-cover"
                        />
                      ) : (
                        <div className="grid h-9 w-9 place-items-center rounded-full bg-muted text-xs text-muted-foreground">
                          —
                        </div>
                      )}
                      <span className="whitespace-nowrap font-medium">
                        {b.coach_name || "—"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full px-2 py-0.5 text-xs capitalize",
                        kindBadgeClass(b.kind)
                      )}
                    >
                      {b.kind}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-md text-sm text-muted-foreground">
                    {previewText(b)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {(b.recipientCount ?? 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {b.readCount ?? 0}/{b.recipientCount ?? 0}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {b.sent_at
                      ? new Date(b.sent_at).toLocaleString()
                      : "—"}
                  </TableCell>
                  <TableCell className="space-x-1 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(b)}
                      aria-label="Edit message"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteBatch(b)}
                      aria-label="Delete message batch"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>

        <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              Page {page}
              {totalPages > 0 ? ` of ${totalPages}` : ""}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={totalPages > 0 && page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Dialog
        open={!!editBatch}
        onOpenChange={(open) => !open && setEditBatch(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit message</DialogTitle>
            <DialogDescription>
              Updates the {editBatch?.kind} content for every recipient of
              this send. Media (audio/video/cover) can't be changed here —
              delete and resend to swap media.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-topic">
                {editBatch?.kind === "short" ||
                editBatch?.kind === "quote" ||
                editBatch?.kind === "text"
                  ? "Headline"
                  : "Topic"}
              </Label>
              <Input
                id="edit-topic"
                value={editForm.topic}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, topic: e.target.value }))
                }
                placeholder="Headline shown above the message"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-body">
                {editBatch?.kind === "voice" || editBatch?.kind === "short"
                  ? "Caption"
                  : "Body"}
              </Label>
              <Textarea
                id="edit-body"
                rows={editBatch?.kind === "text" ? 6 : 4}
                value={editForm.body}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, body: e.target.value }))
                }
                placeholder="The message text"
              />
            </div>
            {editBatch?.kind === "text" && (
              <div className="space-y-2">
                <Label htmlFor="edit-preview">Preview</Label>
                <Input
                  id="edit-preview"
                  value={editForm.preview}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, preview: e.target.value }))
                  }
                  placeholder="Collapsed preview shown in the inbox"
                />
              </div>
            )}
            {/* V-CoachSub Phase 3 UX: post-publish tier flip. Same
                semantics as the compose form — general is universally
                accessible, premium gates to viewers with an active
                per-coach subscription. Seeded from the doc's current
                tier so an unrelated edit (typo fix, caption tweak)
                doesn't silently flip the badge. */}
            <div className="space-y-2">
              <Label>
                Content tier<span className="text-destructive"> *</span>
              </Label>
              <div
                role="radiogroup"
                aria-label="Content tier"
                className="space-y-2"
              >
                {(
                  [
                    {
                      value: "general",
                      label: "General — anyone can view this message",
                    },
                    {
                      value: "premium",
                      label:
                        "Premium — only viewers with an active per-coach subscription to this coach",
                    },
                  ] as const
                ).map((opt) => (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer items-start gap-2 text-sm"
                  >
                    <input
                      type="radio"
                      name="edit-content-tier"
                      value={opt.value}
                      checked={editForm.content_tier === opt.value}
                      onChange={() =>
                        setEditForm((f) => ({ ...f, content_tier: opt.value }))
                      }
                      disabled={updateMut.isPending}
                      className="mt-0.5"
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditBatch(null)}
              disabled={updateMut.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleEditSave} disabled={updateMut.isPending}>
              {updateMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deleteBatch}
        onOpenChange={(open) => !open && setDeleteBatch(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete message batch?</DialogTitle>
            <DialogDescription>
              This removes the send event for{" "}
              {deleteBatch?.coach_name || "this coach"} from every
              recipient's inbox ({deleteBatch?.recipientCount ?? 0}{" "}
              recipient
              {deleteBatch?.recipientCount === 1 ? "" : "s"}). This
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteBatch(null)}
              disabled={deleteMut.isPending}
            >
              Keep batch
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete batch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function SortableTH({
  field,
  label,
  className,
  onSortClick,
  SortIcon,
}: {
  field: string
  label: React.ReactNode
  className?: string
  onSortClick: (field: string) => void
  SortIcon: React.ComponentType<{ field: string }>
}) {
  return (
    <TableHead className={cn("cursor-pointer select-none", className)}>
      <button
        type="button"
        onClick={() => onSortClick(field)}
        className="flex items-center gap-1 text-left font-medium text-muted-foreground hover:text-foreground"
      >
        {label}
        <SortIcon field={field} />
      </button>
    </TableHead>
  )
}
